/**
 * Tetris engine — pure game logic for the hidden arcade (C).
 * No DOM, no MobX. createGame() returns a state; the component calls
 * step(state, input, dt) once per fixed tick and drains state.events.
 * 10×20 well, SRS rotation with wall kicks, 7-bag, NES gravity and scoring.
 */

export const COLS = 10;
export const ROWS = 20;
export const CELL = 8;
export const BOARD_WIDTH = COLS * CELL;
export const PANEL_WIDTH = 48;
export const WIDTH = BOARD_WIDTH + PANEL_WIDTH;
export const HEIGHT = ROWS * CELL;
export const TICK_MS = 1000 / 60;

export const PIECES = ['I', 'O', 'T', 'S', 'Z', 'J', 'L'];

// SRS orientations 0, R, 2, L as [cx, cy] cells inside the piece box.
export const SHAPES = {
    I: [
        [[0, 1], [1, 1], [2, 1], [3, 1]],
        [[2, 0], [2, 1], [2, 2], [2, 3]],
        [[0, 2], [1, 2], [2, 2], [3, 2]],
        [[1, 0], [1, 1], [1, 2], [1, 3]],
    ],
    O: [
        [[1, 0], [2, 0], [1, 1], [2, 1]],
        [[1, 0], [2, 0], [1, 1], [2, 1]],
        [[1, 0], [2, 0], [1, 1], [2, 1]],
        [[1, 0], [2, 0], [1, 1], [2, 1]],
    ],
    T: [
        [[1, 0], [0, 1], [1, 1], [2, 1]],
        [[1, 0], [1, 1], [2, 1], [1, 2]],
        [[0, 1], [1, 1], [2, 1], [1, 2]],
        [[1, 0], [0, 1], [1, 1], [1, 2]],
    ],
    S: [
        [[1, 0], [2, 0], [0, 1], [1, 1]],
        [[1, 0], [1, 1], [2, 1], [2, 2]],
        [[1, 1], [2, 1], [0, 2], [1, 2]],
        [[0, 0], [0, 1], [1, 1], [1, 2]],
    ],
    Z: [
        [[0, 0], [1, 0], [1, 1], [2, 1]],
        [[2, 0], [1, 1], [2, 1], [1, 2]],
        [[0, 1], [1, 1], [1, 2], [2, 2]],
        [[1, 0], [0, 1], [1, 1], [0, 2]],
    ],
    J: [
        [[0, 0], [0, 1], [1, 1], [2, 1]],
        [[1, 0], [2, 0], [1, 1], [1, 2]],
        [[0, 1], [1, 1], [2, 1], [2, 2]],
        [[1, 0], [1, 1], [0, 2], [1, 2]],
    ],
    L: [
        [[2, 0], [0, 1], [1, 1], [2, 1]],
        [[1, 0], [1, 1], [1, 2], [2, 2]],
        [[0, 1], [1, 1], [2, 1], [0, 2]],
        [[0, 0], [1, 0], [1, 1], [1, 2]],
    ],
};

// Standard SRS wall-kick tables as [dx, dy] with SRS's y-UP convention;
// applyKick() negates dy for our y-down board.
export const KICKS = {
    JLSTZ: {
        '0>1': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
        '1>0': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
        '1>2': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
        '2>1': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
        '2>3': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]],
        '3>2': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
        '3>0': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
        '0>3': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]],
    },
    I: {
        '0>1': [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]],
        '1>0': [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]],
        '1>2': [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]],
        '2>1': [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]],
        '2>3': [[0, 0], [2, 0], [-1, 0], [2, 1], [-1, -2]],
        '3>2': [[0, 0], [-2, 0], [1, 0], [-2, -1], [1, 2]],
        '3>0': [[0, 0], [1, 0], [-2, 0], [1, -2], [-2, 1]],
        '0>3': [[0, 0], [-1, 0], [2, 0], [-1, 2], [2, -1]],
    },
};

// NES frames per row by level (index level-1); the last entry repeats.
export const GRAVITY_FRAMES = [48, 43, 38, 33, 28, 23, 18, 13, 8, 6, 5, 5, 5, 4, 4, 4, 3, 3, 3, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1];
export const SOFT_DROP_FRAMES = 2;
export const DAS_INITIAL = 16;
export const DAS_REPEAT = 6;
export const LINE_POINTS = [0, 40, 100, 300, 1200];
export const SOFT_DROP_POINTS = 1;
export const HARD_DROP_POINTS = 2;
export const LINES_PER_LEVEL = 10;
export const READY_MS = 1000;
export const CLEAR_MS = 300;

export function gravityFrames(level) {
    const index = Math.max(0, Math.min(GRAVITY_FRAMES.length - 1, level - 1));
    return GRAVITY_FRAMES[index];
}

export function pieceCells(piece) {
    return SHAPES[piece.type][piece.rot].map(([cx, cy]) => [piece.x + cx, piece.y + cy]);
}

export function collides(board, type, rot, x, y) {
    const cells = SHAPES[type][rot];
    for (let i = 0; i < cells.length; i += 1) {
        const bx = x + cells[i][0];
        const by = y + cells[i][1];
        if (bx < 0 || bx >= COLS || by >= ROWS) {
            return true;
        }
        if (by >= 0 && board[by * COLS + bx]) {
            return true;
        }
    }
    return false;
}

function shuffledBag(rng) {
    const bag = PIECES.slice();
    for (let i = bag.length - 1; i > 0; i -= 1) {
        const j = Math.min(i, Math.floor(rng() * (i + 1)));
        const tmp = bag[i];
        bag[i] = bag[j];
        bag[j] = tmp;
    }
    return bag;
}

function drawFromBag(state) {
    if (state.bag.length === 0) {
        state.bag = shuffledBag(state.rng);
    }
    return state.bag.pop();
}

export function spawnPiece(state) {
    const type = state.next;
    state.next = drawFromBag(state);
    state.piece = {
        type, rot: 0, x: 3, y: type === 'I' ? -1 : 0,
    };
    state.gravityCounter = 0;
    state.softCounter = 0;
    if (collides(state.board, type, 0, state.piece.x, state.piece.y)) {
        endGame(state);
        return false;
    }
    return true;
}

function endGame(state) {
    state.status = 'gameOver';
    state.events.push({ type: 'gameOver' });
}

export function createGame({ rng = Math.random } = {}) {
    const state = {
        cols: COLS,
        rows: ROWS,
        rng,
        board: new Uint8Array(COLS * ROWS),
        piece: null,
        bag: [],
        next: null,
        score: 0,
        lines: 0,
        level: 1,
        status: 'ready',
        statusMs: READY_MS,
        gravityCounter: 0,
        softCounter: 0,
        das: { dir: null, timer: 0 },
        clearingRows: [],
        startAnnounced: false,
        elapsedMs: 0,
        events: [],
    };
    state.next = drawFromBag(state);
    return state;
}

function tryMove(state, dx, dy) {
    const p = state.piece;
    if (collides(state.board, p.type, p.rot, p.x + dx, p.y + dy)) {
        return false;
    }
    p.x += dx;
    p.y += dy;
    return true;
}

function tryRotate(state, direction) {
    const p = state.piece;
    const from = p.rot;
    const to = (p.rot + direction + 4) % 4;
    const table = p.type === 'I' ? KICKS.I : KICKS.JLSTZ;
    const kicks = table[`${from}>${to}`] || [[0, 0]];
    for (let i = 0; i < kicks.length; i += 1) {
        const nx = p.x + kicks[i][0];
        const ny = p.y - kicks[i][1]; // SRS y-up → board y-down
        if (!collides(state.board, p.type, to, nx, ny)) {
            p.rot = to;
            p.x = nx;
            p.y = ny;
            state.events.push({ type: 'rotate' });
            return true;
        }
    }
    return false;
}

function addScore(state, points) {
    state.score += points;
}

function lockPiece(state) {
    const cells = pieceCells(state.piece);
    let lockedOut = false;
    cells.forEach(([bx, by]) => {
        if (by < 0) {
            lockedOut = true;
            return;
        }
        state.board[by * COLS + bx] = PIECES.indexOf(state.piece.type) + 1;
    });
    state.events.push({ type: 'lock' });
    state.piece = null;
    if (lockedOut) {
        endGame(state);
        return;
    }
    const full = [];
    for (let row = 0; row < ROWS; row += 1) {
        let complete = true;
        for (let col = 0; col < COLS; col += 1) {
            if (!state.board[row * COLS + col]) {
                complete = false;
                break;
            }
        }
        if (complete) {
            full.push(row);
        }
    }
    if (full.length > 0) {
        state.clearingRows = full;
        state.status = 'clearing';
        state.statusMs = CLEAR_MS;
        state.events.push({ type: 'lineClear', count: full.length });
        return;
    }
    spawnPiece(state);
}

function collapseRows(state) {
    const rows = state.clearingRows;
    const next = new Uint8Array(COLS * ROWS);
    let target = ROWS - 1;
    for (let row = ROWS - 1; row >= 0; row -= 1) {
        if (rows.includes(row)) {
            continue;
        }
        for (let col = 0; col < COLS; col += 1) {
            next[target * COLS + col] = state.board[row * COLS + col];
        }
        target -= 1;
    }
    state.board = next;
    const count = rows.length;
    addScore(state, (LINE_POINTS[Math.min(count, LINE_POINTS.length - 1)] || 0) * state.level);
    state.lines += count;
    const newLevel = 1 + Math.floor(state.lines / LINES_PER_LEVEL);
    if (newLevel > state.level) {
        state.level = newLevel;
        state.events.push({ type: 'levelUp', level: newLevel });
    }
    state.clearingRows = [];
    state.status = 'playing';
    spawnPiece(state);
}

function handleHorizontal(state, input) {
    let dir = null;
    if (input.right) {
        dir = 'right';
    } else if (input.left) {
        dir = 'left';
    }
    if (!dir) {
        state.das.dir = null;
        state.das.timer = 0;
        return;
    }
    const dx = dir === 'right' ? 1 : -1;
    if (state.das.dir !== dir) {
        state.das.dir = dir;
        state.das.timer = DAS_INITIAL;
        if (tryMove(state, dx, 0)) {
            state.events.push({ type: 'move' });
        }
        return;
    }
    state.das.timer -= 1;
    if (state.das.timer <= 0) {
        state.das.timer = DAS_REPEAT;
        if (tryMove(state, dx, 0)) {
            state.events.push({ type: 'move' });
        }
    }
}

function hardDrop(state) {
    let cells = 0;
    while (tryMove(state, 0, 1)) {
        cells += 1;
    }
    addScore(state, cells * HARD_DROP_POINTS);
    lockPiece(state);
}

function countdown(state, dtMs) {
    state.statusMs -= dtMs;
    return state.statusMs <= 0;
}

export function step(state, input = {}, dtMs = TICK_MS) {
    if (state.status === 'gameOver') {
        return state;
    }
    state.elapsedMs += dtMs;

    if (state.status === 'ready') {
        if (!state.startAnnounced) {
            state.startAnnounced = true;
            state.events.push({ type: 'start' });
        }
        if (countdown(state, dtMs)) {
            state.status = 'playing';
            spawnPiece(state);
        }
        return state;
    }
    if (state.status === 'clearing') {
        if (countdown(state, dtMs)) {
            collapseRows(state);
        }
        return state;
    }
    if (!state.piece) {
        return state;
    }

    handleHorizontal(state, input);
    if (state.status !== 'playing') {
        return state;
    }
    if (input.rotateCw) {
        tryRotate(state, 1);
    } else if (input.rotateCcw) {
        tryRotate(state, -1);
    }
    if (input.hardDrop) {
        hardDrop(state);
        return state;
    }

    // Soft drop
    if (input.down) {
        state.softCounter += 1;
        if (state.softCounter >= SOFT_DROP_FRAMES) {
            state.softCounter = 0;
            if (tryMove(state, 0, 1)) {
                addScore(state, SOFT_DROP_POINTS);
                state.gravityCounter = 0;
            }
        }
    } else {
        state.softCounter = 0;
    }

    // Gravity
    state.gravityCounter += 1;
    if (state.gravityCounter >= gravityFrames(state.level)) {
        state.gravityCounter = 0;
        if (!tryMove(state, 0, 1)) {
            lockPiece(state);
        }
    }
    return state;
}
